import React, { useId } from 'react';

/* Brand marks drawn to match the iOS trainer apps.
   Each mark fills with the accent violet→cyan gradient and carries
   the same violet glow the SwiftUI Theme.Ink.glow() modifier applies. */

const GRADIENT_ID = 'brand-accent-grad';
const GLOW_ID = 'brand-accent-glow';

export function BrandDefs() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
      <defs>
        <linearGradient id={GRADIENT_ID} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgb(140, 92, 245)" />
          <stop offset="100%" stopColor="rgb(61, 209, 240)" />
        </linearGradient>
        <filter id={GLOW_ID} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </svg>
  );
}

/* PokerTrainer — the spade in the app's brand lockup. */
export function PokerMark({ size = 120, glow = true }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <text
        x="60"
        y="76"
        textAnchor="middle"
        fontFamily="Georgia, 'Apple Symbols', serif"
        fontSize="96"
        fontWeight="900"
        fill={`url(#${GRADIENT_ID})`}
        filter={glow ? `url(#${GLOW_ID})` : undefined}
      >
        ♠
      </text>
    </svg>
  );
}

/* BlackjackTrainer — the "21" figure the app is named for. */
export function BlackjackMark({ size = 120, glow = true }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <text
        x="60"
        y="82"
        textAnchor="middle"
        fontFamily="'Nunito', ui-rounded, -apple-system, sans-serif"
        fontSize="72"
        fontWeight="900"
        letterSpacing="-2"
        fill={`url(#${GRADIENT_ID})`}
        filter={glow ? `url(#${GLOW_ID})` : undefined}
      >
        21
      </text>
    </svg>
  );
}

/* Where on Earth! — a wireframe globe with a lit rim.
   The proportions below are the exact values in the iOS app's
   BrandGlyph.swift, so the two marks are drawn from one description.
   The gradient is defined locally in userSpaceOnUse because the shared
   defs use objectBoundingBox, which collapses on horizontal-parallel
   strokes with zero-height bboxes and paints them transparent. */
export function WhereOnEarthMark({ size = 120, glow = true }) {
  const uid = useId().replace(/:/g, '');
  const gradId = `worlde-grad-${uid}`;
  const glowId = `worlde-glow-${uid}`;

  const sphereRadiusFraction = 0.92;
  const radius = (size / 2) * sphereRadiusFraction;
  const cx = size / 2;
  const cy = size / 2;

  const limbStroke = 0.055 * radius;
  const gridStroke = 0.04 * radius;
  const litStroke = 0.10 * radius;
  const samples = 96;

  // Order matches BrandGlyph.arcs — limb, meridians, parallels, then the lit rim on top.
  const arcs = [
    { hw: 1, hh: 1, ch: 0, start: 0, end: 360, stroke: limbStroke },
    ...[0, 0.55].map(hw => ({ hw, hh: 1, ch: 0, start: 0, end: 360, stroke: gridStroke })),
    ...[-0.66, -0.34, 0, 0.34, 0.66].map(h => ({
      hw: Math.sqrt(1 - h * h), hh: 0, ch: h,
      start: 0, end: 180, stroke: gridStroke,
    })),
    { hw: 1, hh: 1, ch: 0, start: 184, end: 292, stroke: litStroke },
  ];

  const arcPath = arc => {
    const pts = [];
    for (let i = 0; i <= samples; i++) {
      const rad = (arc.start + (arc.end - arc.start) * i / samples) * Math.PI / 180;
      pts.push([
        (cx + arc.hw * Math.cos(rad) * radius).toFixed(2),
        (cy + (arc.ch + arc.hh * Math.sin(rad)) * radius).toFixed(2),
      ]);
    }
    return 'M' + pts.map(([x, y]) => `${x},${y}`).join('L');
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={gradId}
          x1="0" y1="0" x2={size} y2={size}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="rgb(140, 92, 245)" />
          <stop offset="100%" stopColor="rgb(61, 209, 240)" />
        </linearGradient>
        <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation={size * 0.03} result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g
        stroke={`url(#${gradId})`}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={glow ? `url(#${glowId})` : undefined}
      >
        {arcs.map((arc, i) => (
          <path key={i} d={arcPath(arc)} strokeWidth={arc.stroke} />
        ))}
      </g>
    </svg>
  );
}

/* BridgeTrainer — four suits arranged in a diamond, drawn the way
   BrandMarkView.diamond() lays them out in the app. */
export function BridgeMark({ size = 120, glow = true }) {
  const r = 26;
  const cx = 60, cy = 60;
  const suit = (symbol, dx, dy) => (
    <text
      x={cx + dx}
      y={cy + dy + 12}
      textAnchor="middle"
      fontFamily="Georgia, 'Apple Symbols', serif"
      fontSize="36"
      fontWeight="900"
    >
      {symbol}
    </text>
  );
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g fill={`url(#${GRADIENT_ID})`} filter={glow ? `url(#${GLOW_ID})` : undefined}>
        {suit('♠', 0, -r)}
        {suit('♥', r, 0)}
        {suit('♦', 0, r)}
        {suit('♣', -r, 0)}
      </g>
    </svg>
  );
}

/* Small suit-diamond used as the site's own header mark. */
export function SuitDiamondMark({ size = 28 }) {
  const r = size * 0.30;
  const cx = size / 2, cy = size / 2;
  const fontSize = size * 0.42;
  const suit = (symbol, dx, dy) => (
    <text
      x={cx + dx}
      y={cy + dy + fontSize * 0.36}
      textAnchor="middle"
      fontFamily="Georgia, 'Apple Symbols', serif"
      fontSize={fontSize}
      fontWeight="900"
    >
      {symbol}
    </text>
  );
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g fill={`url(#${GRADIENT_ID})`}>
        {suit('♠', 0, -r)}
        {suit('♥', r, 0)}
        {suit('♦', 0, r)}
        {suit('♣', -r, 0)}
      </g>
    </svg>
  );
}
